import React from "react";
import { useI18n } from "@/i18n";
import { useSiteContent } from "@/lib/siteContent";

const LUCILE_FALLBACK = "https://customer-assets-agu9un31.emergentagent.net/job_chateau-escapes/artifacts/1toukn87_lucile.JPG";
const PASCAL_FALLBACK = "https://customer-assets-agu9un31.emergentagent.net/job_chateau-escapes/artifacts/dxng3rw5_pascal.JPG";
const CAMILLE_FALLBACK = "https://customer-assets-agu9un31.emergentagent.net/job_chateau-escapes/artifacts/xzsu8euq_camille.JPG";

export default function About() {
  const { t, lang } = useI18n();
  const { categoriesBySlug, blocks } = useSiteContent();
  const cat = categoriesBySlug["about"];

  // Prefer category data (new CMS). Fall back to legacy blocks + team_d translations.
  const title = cat?.[`title_${lang}`] || cat?.title_fr || blocks["about.title"]?.[lang] || blocks["about.title"]?.fr || t("about.title");
  const lede = cat?.[`intro_${lang}`] || cat?.intro_fr || blocks["about.lede"]?.[lang] || blocks["about.lede"]?.fr || t("about.lede");

  let team = [];
  if (cat && cat.kind === "team" && Array.isArray(cat.items) && cat.items.length > 0) {
    team = [...cat.items]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((it) => ({
        name: it.name,
        role: it[`role_${lang}`] || it.role_fr,
        bio: it[`body_${lang}`] || it.body_fr,
        img: it.image,
      }));
  } else {
    const pick = (key, fb) => blocks[key]?.[lang] || blocks[key]?.fr || fb;
    team = [
      { name: "Lucile", role: t("about.lucile"), bio: pick("about.lucile.bio", t("about.lucile_d")), img: pick("about.lucile.image", LUCILE_FALLBACK) },
      { name: "Pascal", role: t("about.pascal"), bio: pick("about.pascal.bio", t("about.pascal_d")), img: pick("about.pascal.image", PASCAL_FALLBACK) },
      { name: "Camille", role: t("about.camille"), bio: pick("about.camille.bio", t("about.camille_d")), img: pick("about.camille.image", CAMILLE_FALLBACK) },
    ];
  }

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-24">
        <div className="overline text-[#D4AF37]">{lang === "fr" ? "Notre maison" : "Our house"}</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl text-[#0A1128] mt-6 max-w-4xl leading-[1.02]" data-testid="about-title">
          {title}
        </h1>
        <p className="mt-10 font-serif italic text-2xl md:text-3xl text-[#0A1128]/80 max-w-3xl">{lede}</p>
      </section>
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-32 grid grid-cols-1 md:grid-cols-3 gap-12" data-testid="team-grid">
        {team.map((p, i) => (
          <div key={`${p.name}-${i}`} className={i % 3 === 1 ? "md:mt-24" : ""} data-testid={`team-member-${i}`}>
            <div className="hover-scale overflow-hidden bg-[#0A1128]/5">
              {p.img ? <img src={p.img} alt={p.name} className="w-full h-[500px] object-cover" /> : <div className="w-full h-[500px]" />}
            </div>
            <div className="mt-6">
              <div className="overline text-[#D4AF37]">{p.role}</div>
              <h3 className="font-serif text-3xl text-[#0A1128] mt-2">{p.name}</h3>
              <p className="text-sm text-[#0A1128]/70 mt-3 leading-relaxed">{p.bio}</p>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
